import React, { useState, useEffect } from 'react';
import { testimonials as mockTestimonials } from '../mock';
import { Star, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Testimonials = ({ onBookingClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [testimonials, setTestimonials] = useState(mockTestimonials);

  // Fetch testimonials from API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${API_URL}/api/testimonials`);
        if (response.ok) {
          const data = await response.json();
          if (data.testimonials && data.testimonials.length > 0) {
            // Map API data to component format
            setTestimonials(data.testimonials.map(t => ({
              id: t.id,
              text: t.text,
              author: t.author,
              role: t.role,
              rating: t.rating,
              image: t.image,
              isLogo: t.is_logo
            })));
          }
        }
      } catch (error) {
        console.log('Using mock testimonials data');
      }
    };
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (!isPaused && testimonials.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isPaused, testimonials.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const testimonial = testimonials[currentIndex];

  return (
    <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-black border-2 border-red rounded-3xl mb-8">
      <div className="container mx-auto">
        {/* Header with 5 Star Emphasis */}
        <div className="text-center mb-12">
          <h2 className="text-xl sm:text-[3.2vw] md:text-[3vw] lg:text-[2.85vw] text-center mb-6 text-gold font-semibold sm:whitespace-nowrap" style={{ textTransform: 'none', fontFamily: 'Montserrat, sans-serif' }}>
            What People Say
          </h2>
          
          {/* Large 5 Star Display - Solid Gold */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} fill="#D4AF37" stroke="#D4AF37" className="drop-shadow-[0_0_10px_rgba(212,175,55,0.8)]" size={40} />
              ))}
            </div>
            <p className="text-white text-lg font-medium tracking-wide">5 Star Reviews</p>
          </div>
        </div>
        
        {/* Main Testimonial Card */}
        <div 
          className="max-w-4xl mx-auto relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="relative bg-gradient-to-br from-black via-black to-red/10 p-10 lg:p-14 rounded-3xl border border-gold/40 shadow-[0_0_60px_rgba(212,175,55,0.15)] overflow-hidden">
            {/* Background Glow Effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
            
            {/* Stars Row - Solid Gold */}
            <div className="flex justify-center gap-1.5 mb-8 relative z-10">
              {[...Array(5)].map((_, i) => (
                <Star key={i} fill="#D4AF37" stroke="#D4AF37" size={28} />
              ))}
            </div>
            
            {/* Testimonial Text */}
            <blockquote className="text-xl lg:text-2xl xl:text-3xl text-center text-white mb-8 italic font-light leading-relaxed relative z-10">
              "{testimonial.text}"
            </blockquote>
            
            {/* Author Section with Image */}
            <div className="flex flex-col items-center relative z-10">
              {testimonial.image && (
                <div className={testimonial.isLogo 
                  ? "mb-4 p-2 border-4 border-gold rounded-lg shadow-[0_0_30px_rgba(212,175,55,0.4)] bg-white"
                  : "mb-4"
                }>
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.author}
                    className={testimonial.isLogo 
                      ? "w-20 h-20 sm:w-24 sm:h-24 object-contain"
                      : "w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-gold shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                    }
                  />
                </div>
              )}
              <div className="text-center">
                <p className="text-gold font-bold text-xl tracking-wide">{testimonial.author}</p>
                {testimonial.role && (
                  <>
                    <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-gold to-transparent mt-2"></div>
                    <p className="text-silver text-sm mt-2 uppercase tracking-widest">{testimonial.role}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          {testimonials.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="nav-arrow-static absolute bg-gold text-black border-2 border-gold p-3 rounded-full shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                style={{ left: 0, top: '50%', transform: 'translateY(-50%) translateX(-0.5rem)' }}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goToNext}
                className="nav-arrow-static absolute bg-gold text-black border-2 border-gold p-3 rounded-full shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                style={{ right: 0, top: '50%', transform: 'translateY(-50%) translateX(0.5rem)' }}
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        {/* Dots Navigation */}
        {testimonials.length > 1 && (
          <div className="flex justify-center gap-3 mt-10">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentIndex 
                    ? 'bg-gold w-10 shadow-[0_0_10px_rgba(212,175,55,0.8)]' 
                    : 'bg-gold/30 w-2 hover:bg-gold/60'
                }`}
              />
            ))}
          </div>
        )}

        {/* CTA Button */}
        <div className="mt-12 text-center">
          <Button
            onClick={onBookingClick}
            className="bg-gold text-black hover:bg-gold/90 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] hover:scale-105"
            data-testid="testimonials-cta-btn"
          >
            <Calendar className="mr-2" size={20} />
            Check Availability & Book
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
